// src/lib/taskDueDate.ts
//
// A task "due 2026-10-05" means due by the END of that day in Melbourne, not at
// UTC midnight. `new Date("2026-10-05").toISOString()` (and a bare date string
// cast to timestamptz) lands at 00:00Z = 10:00/11:00 Melbourne time, so a task
// due "today" escalated mid-morning. This resolves 23:59:59 Australia/Melbourne
// with the correct AEST (+10:00) / AEDT (+11:00) offset for that date.

export const TASK_DUE_TIME_ZONE = "Australia/Melbourne";

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Offset of `timeZone` from UTC at `instantMs`, in minutes (east positive). */
function zoneOffsetMinutes(instantMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instantMs));
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  const wallAsUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return Math.round((wallAsUtc - Math.floor(instantMs / 1000) * 1000) / 60000);
}

function formatOffset(minutes: number): string {
  const sign = minutes >= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${sign}${hh}:${mm}`;
}

/**
 * `"2026-07-15"` → `"2026-07-15T23:59:59+10:00"` (AEST);
 * `"2026-01-15"` → `"2026-01-15T23:59:59+11:00"` (AEDT).
 *
 * Independent of the browser's own time zone. Throws on anything that is not a
 * real YYYY-MM-DD date, so a malformed value can never silently become a
 * different deadline.
 */
export function endOfDayMelbourneIso(dateOnly: string): string {
  const m = DATE_ONLY.exec(dateOnly);
  if (!m) throw new Error(`Expected a YYYY-MM-DD date, got "${dateOnly}"`);
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const wallAsUtc = Date.UTC(y, mo - 1, d, 23, 59, 59);
  const check = new Date(wallAsUtc);
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) {
    throw new Error(`Not a real calendar date: "${dateOnly}"`);
  }
  // Two passes: guess with the offset 10h before, then correct with the offset
  // at the resulting instant. 23:59:59 is never inside a DST transition (those
  // happen at 02:00/03:00), so this converges.
  let offset = zoneOffsetMinutes(wallAsUtc - 10 * 3600_000, TASK_DUE_TIME_ZONE);
  offset = zoneOffsetMinutes(wallAsUtc - offset * 60_000, TASK_DUE_TIME_ZONE);
  return `${dateOnly}T23:59:59${formatOffset(offset)}`;
}
